/**
 * OperatorAgentPane — the FORGE Operator Agent chat (S0): a grounded, conversational
 * driver for ONE tender/product. You ask it about the spec, suppliers, quotes, and next
 * steps; it answers from the item's live state. Actions (edit / source / award via
 * inline cards) land in later slices. Reuses the .forge-accio-* messaging styling.
 *
 * S0 is synchronous request/response (no bridge, no polling): send → await reply → show.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Sparkles, RefreshCw } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import Markdown from '../../../components/Markdown';
import ChatArtifact from './ChatArtifact';

const fmtTime = (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const SUGGESTIONS = [
  "What's the status of this bid?",
  'Summarize the line items',
  'Which suppliers do we have and where do they stand?',
  "What's blocking submission?",
];

const OperatorAgentPane = ({ tenderId = '' }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await forgeAPI.getAgentChat(tenderId);
      setMessages(d.messages || []);
    } catch { /* transient */ }
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); },
    [messages.length, busy]);

  const onAction = async (action, params) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const res = await forgeAPI.agentAction(tenderId, action, params);
      if (res.messages) setMessages(res.messages);
      else await load();
      if (res.ok === false) setErr(res.error || 'Action failed.');
    } catch { setErr('Could not complete the action.'); }
    finally { setBusy(false); }
  };

  // Drag a spec PDF onto the chat: upload it, then let the agent surface the extracted
  // line items (product design) or acknowledge the document (tender).
  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file || busy) return;
    setBusy(true); setErr(null);
    setMessages((prev) => [...prev,
      { message_id: `local-${Date.now()}`, sender: 'operator', body: `📎 ${file.name}`, ts: new Date().toISOString() }]);
    try {
      const isProduct = String(tenderId).startsWith('prod_');
      if (isProduct && file.type.startsWith('image/')) {
        await forgeAPI.uploadProductImage(tenderId, file, 0);
        const r = await forgeAPI.sendAgentChat(tenderId, `I attached a reference image (${file.name}) to this product — it'll be shared with suppliers during sourcing.`);
        setMessages(r.messages || []);
      } else if (isProduct) {
        await forgeAPI.uploadProductSpec(tenderId, file, false);
        const r = await forgeAPI.sendAgentChat(tenderId, `I dropped a spec PDF (${file.name}) — show the line items you extracted and offer to send it to sourcing.`);
        setMessages(r.messages || []);
      } else {
        await forgeAPI.uploadWorkingDoc(tenderId, file);
        const r = await forgeAPI.sendAgentChat(tenderId, `I uploaded a document (${file.name}) to this tender.`);
        setMessages(r.messages || []);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not upload/extract that file.');
    } finally { setBusy(false); }
  };

  const send = async (text) => {
    const body = (text ?? draft).trim();
    if (!body || busy) return;
    setBusy(true); setErr(null); setDraft('');
    setMessages((prev) => [...prev,
      { message_id: `local-${Date.now()}`, sender: 'operator', body, ts: new Date().toISOString() }]);
    try {
      const res = await forgeAPI.sendAgentChat(tenderId, body);
      setMessages(res.messages || []);
    } catch {
      setErr('Could not reach the FORGE agent — try again.');
    } finally { setBusy(false); }
  };

  // group consecutive same-sender messages under one head (avatar + name + time)
  const items = [];
  let lastSender = null, lastTs = 0;
  messages.forEach((m) => {
    const t = m.ts ? new Date(m.ts).getTime() : Date.now();
    const grouped = m.sender === lastSender && (t - lastTs) < 5 * 60000;
    items.push({ m, t, head: !grouped });
    lastSender = m.sender; lastTs = t;
  });

  return (
    <div className="forge-accio"
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={onDrop}>
      {dragOver && (
        <div className="forge-accio-drop"><span>⤓ Drop a spec PDF to add it to this {String(tenderId).startsWith('prod_') ? 'product' : 'bid'}</span></div>
      )}
      <div className="forge-accio-head">
        <span className="forge-accio-hd-av"><Sparkles size={16} /><span className={`forge-accio-dot ${busy ? 'on' : ''}`} /></span>
        <div className="forge-accio-hd-meta">
          <b>FORGE Agent</b>
          <span className="forge-accio-hd-status">{busy ? 'thinking…' : 'grounded in this bid'}</span>
        </div>
        <button className="forge-accio-refresh" onClick={load} title="Refresh"><RefreshCw size={13} /></button>
      </div>

      <div className="forge-accio-log">
        {items.length === 0 && !busy && (
          <div className="forge-accio-empty">
            <span className="forge-accio-empty-av"><Sparkles size={22} /></span>
            Ask me anything about this bid — the spec, suppliers, quotes, or what to do next.
          </div>
        )}

        {items.map(({ m, t, head }) => {
          const out = m.sender === 'operator';
          return (
            <div key={m.message_id} className={`forge-accio-row ${out ? 'out' : 'in'} ${head ? 'head' : 'cont'}`}>
              {!out && <div className="forge-accio-av">{head && <Sparkles size={15} />}</div>}
              <div className="forge-accio-col">
                {head && (
                  <div className="forge-accio-meta">
                    <span className="forge-accio-name">{out ? 'You' : 'FORGE Agent'}</span>
                    <span className="forge-accio-time">{fmtTime(t)}</span>
                  </div>
                )}
                <div className={`forge-accio-bubble ${out ? 'out' : 'in'} ${head ? 'head' : ''}`}>
                  <Markdown>{m.body || ''}</Markdown>
                </div>
                {(m.artifacts || []).map((a, i) => (
                  <ChatArtifact key={`${m.message_id}-art-${i}`} artifact={a} onAction={onAction} busy={busy} />
                ))}
              </div>
            </div>
          );
        })}

        {busy && (
          <div className="forge-accio-row in head">
            <div className="forge-accio-av"><Sparkles size={15} /></div>
            <div className="forge-accio-col">
              <div className="forge-accio-bubble in head forge-accio-working">
                <span className="forge-accio-typing"><i /><i /><i /></span>
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {items.length === 0 && (
        <div className="forge-accio-chips">
          {SUGGESTIONS.map((c, i) => (
            <button key={i} className="forge-accio-chip" disabled={busy} onClick={() => send(c)}>{c}</button>
          ))}
        </div>
      )}

      {err && <div className="forge-accio-err">{err}</div>}

      <div className="forge-accio-composer">
        <div className="forge-accio-inputwrap">
          <textarea
            rows={1}
            value={draft}
            placeholder="Ask the FORGE agent…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <button className="forge-accio-send" disabled={busy || !draft.trim()} onClick={() => send()} title="Send">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperatorAgentPane;
